import axios from "axios";
import { APP_CONFIG } from "../config/config";
import { ENDPOINT } from "./api-endpoint";


/**
 * GET endpoints. Server wraps payloads as `{ status: 'success', data: T }`.
 * T may be: a plain array, `{ data: rows[], count }` (paginated), or an object (e.g. dashboard).
 * Older code used `RESPONSE.data.data`, which is wrong when T is already the array (`data` is the array).
 */
export async function getList(apiURL: string, params?: any) {
    try {
        const REQUEST_URL = `${APP_CONFIG.API}${apiURL}`;        
        const RESPONSE = await axios.get(REQUEST_URL, { params });
        const body = RESPONSE?.data ?? {};

        const inner =
            typeof body === "object" &&
            body !== null &&
            (body as { status?: string }).status === "success" &&
            (body as { data?: unknown }).data !== undefined
                ? (body as { data: unknown }).data
                : body;

        if (inner === null || inner === undefined) {
            return [];
        }

        if (Array.isArray(inner)) {
            return inner;
        }

        if (
            typeof inner === "object" &&
            Array.isArray((inner as { data?: unknown }).data) &&
            Object.prototype.hasOwnProperty.call(inner, "count")
        ) {
            return inner;
        }

        return inner;
    }
    catch(error) {
        return [];
    }
}

/**
 * Retrieves list data from the API
 */
/**
 * POST list endpoints. Backend uses responseWrapper: `{ status, data: T }`.
 * Paginated handlers return `T = { data: rows[], count }`, so rows live at
 * `response.data.data.data` after axios (not `response.data.data` alone).
 */
export async function getLists(apiURL: string, request: any) {
    try {
        const REQUEST_URL = `${APP_CONFIG.API}${apiURL}`;        
        const RESPONSE = await axios.post(REQUEST_URL, request);
        const body = RESPONSE?.data ?? {};

        const payload =
            body &&
            typeof body === "object" &&
            (body as { status?: string }).status === "success" &&
            (body as { data?: unknown }).data !== undefined
                ? (body as { data: unknown }).data
                : body;

        if (
            payload &&
            typeof payload === "object" &&
            Array.isArray((payload as { data?: unknown }).data) &&
            Object.prototype.hasOwnProperty.call(payload, "count")
        ) {
            const p = payload as { data: unknown[]; count?: number };
            return {
                data: p.data,
                count: Number(p.count ?? 0),
            };
        }

        return { data: [] as unknown[], count: 0 };
    }
    catch(error) {
        return { data: [] as unknown[], count: 0 };
    }
}

/**
 * Retrieves id base data from the API  
 * @returns 
 */
export async function getListById(apiURL: string, id: any) {
    try {
        const REQUEST_URL = `${APP_CONFIG.API}${apiURL}/${id}`;        
        const RESPONSE = await axios.get(REQUEST_URL);
        const body = RESPONSE?.data ?? {};

        const inner =
            typeof body === "object" &&
            body !== null &&
            (body as { status?: string }).status === "success" &&
            (body as { data?: unknown }).data !== undefined
                ? (body as { data: unknown }).data
                : body;

        return inner;
    }
    catch(error) {
        return null;
    }
}

/**
 * Retrieves add data from the API  
 * @returns 
 */
export async function addList(apiURL: string, request: any) {
    try {
        const REQUEST_URL = `${APP_CONFIG.API}${apiURL}`;        
        const RESPONSE = await axios.post(REQUEST_URL, request);
        const data = RESPONSE?.data?.status;

        return data;
    }
    catch(error) {
        return [];
    }
}

/** POST /job/add — throws with server message on failure (e.g. DB errors). */
export async function postJobAdd(request: Record<string, unknown>): Promise<void> {
  try {
    const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.JOB_ADD}`;
    const RESPONSE = await axios.post(REQUEST_URL, request);
    const body = RESPONSE?.data ?? {};
    if (body.status === "error") {
      throw new Error(
        typeof body.message === "string" ? body.message : "Failed to create job",
      );
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { message?: string; status?: string } | undefined;
      if (data?.status === "error" && typeof data.message === "string") {
        throw new Error(data.message);
      }
      const msg =
        (data && typeof data.message === "string" && data.message) ||
        error.message ||
        "Failed to create job";
      throw new Error(String(msg));
    }
    throw error;
  }
}

export type JobChecklistItemDto = {
  workInstructionId: number;
  checklistItemKey?: string;
  label: string;
  checked: boolean;
};

export type JobGuidedStepDto = {
  jobId: number;
  jobname: string | null;
  recipeId: number | null;
  stepIndex: number;
  stepTitle: string;
  status: string;
  checklist: JobChecklistItemDto[];
  checklistProgressPct: number;
  setupCompleteAt: string | null;
  totalSteps: number;
  workflowStepCompleted: number;
  workflowStepTotal: number;
};

function unwrapApiData<T>(body: unknown): T {
  if (
    typeof body === 'object' &&
    body !== null &&
    (body as { status?: string }).status === 'success' &&
    (body as { data?: unknown }).data !== undefined
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/** GET /job/guided/:jobId/:stepIndex */
export async function getJobGuidedStep(
  jobId: number,
  stepIndex: number,
): Promise<JobGuidedStepDto | null> {
  try {
    const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.JOB_GUIDED}/${jobId}/${stepIndex}`;
    const RESPONSE = await axios.get(REQUEST_URL);
    return unwrapApiData<JobGuidedStepDto>(RESPONSE?.data ?? {});
  } catch {
    return null;
  }
}

/** PATCH /job/guided/:jobId/checklist */
export async function patchJobGuidedChecklist(
  jobId: number,
  request: { stepIndex: number; workInstructionId: number; checked: boolean },
): Promise<{
  ok: boolean;
  status: string;
  checklist: JobChecklistItemDto[];
  checklistProgressPct: number;
  workflowStepCompleted: number;
  workflowStepTotal: number;
  jobStatus: string;
}> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.JOB_GUIDED}/${jobId}/checklist`;
  const RESPONSE = await axios.patch(REQUEST_URL, request);
  const body = RESPONSE?.data ?? {};
  if (body.status === 'error') {
    throw new Error(typeof body.message === 'string' ? body.message : 'Failed to update checklist');
  }
  return unwrapApiData(RESPONSE.data);
}

/** POST /job/guided/:jobId/complete-setup */
export async function postJobGuidedCompleteSetup(
  jobId: number,
  request: { stepIndex: number },
): Promise<{
  ok: boolean;
  status: string;
  checklistProgressPct: number;
  workflowStepCompleted: number;
  workflowStepTotal: number;
  jobStatus: string;
}> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.JOB_GUIDED}/${jobId}/complete-setup`;
  const RESPONSE = await axios.post(REQUEST_URL, request);
  const body = RESPONSE?.data ?? {};
  if (body.status === 'error') {
    throw new Error(typeof body.message === 'string' ? body.message : 'Failed to complete setup');
  }
  return unwrapApiData(RESPONSE.data);
}

/** POST /skill/add — throws with server message on failure (e.g. duplicate). */
export async function postSkill(request: { skillname: string }): Promise<{
  id: number;
  skillname: string;
}> {
  try {
    const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.SKILL_ADD}`;
    const RESPONSE = await axios.post(REQUEST_URL, request);
    const body = RESPONSE?.data ?? {};
    if (body.status === 'error') {
      throw new Error(
        typeof body.message === 'string' ? body.message : 'Failed to add skill',
      );
    }
    const inner =
      body.status === 'success' && body.data !== undefined ? body.data : body;
    return inner as { id: number; skillname: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { message?: string } | undefined;
      const msg = data?.message ?? error.message ?? 'Failed to add skill';
      throw new Error(String(msg));
    }
    throw error;
  }
}

export type RecipeSaveRequest = {
  id?: number;
  workflowId: number;
  name: string;
  materialCategoryId?: number | null;
  materialGradeId?: number | null;
  materialGradeIds?: number[];
  status: 'draft' | 'approved' | 'pending';
  payload: {
    cells: Record<string, string>;
    workflowOperationIds?: number[];
    materialGradeIds?: number[];
    submittedForReview?: boolean;
    submittedForApproval?: boolean;
  };
};

export type RecipeWorkInstructionDto = {
  id: number;
  recipeId: number;
  sortOrder: number;
  title: string;
  description?: string | null;
  safetyNotes?: string | null;
  estimatedTime?: string | null;
};

export async function postRecipeWorkInstruction(request: {
  recipeId: number;
  title: string;
  description?: string;
  safetyNotes?: string;
  estimatedTime?: string;
}): Promise<RecipeWorkInstructionDto> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.RECIPE_WORK_INSTRUCTIONS}`;
  const RESPONSE = await axios.post(REQUEST_URL, request);
  const body = RESPONSE.data ?? {};
  if (body.status === 'error') {
    throw new Error(typeof body.message === 'string' ? body.message : 'Failed to add instruction');
  }
  const inner =
    body.status === 'success' && body.data !== undefined ? body.data : body;
  return inner as RecipeWorkInstructionDto;
}

export async function patchRecipeWorkInstructionReorder(request: {
  recipeId: number;
  orderedIds: number[];
}): Promise<{ ok: boolean }> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.RECIPE_WORK_INSTRUCTIONS_REORDER}`;
  const RESPONSE = await axios.patch(REQUEST_URL, request);
  return RESPONSE.data as { ok: boolean };
}

export async function deleteRecipeWorkInstruction(id: number): Promise<void> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.RECIPE_WORK_INSTRUCTIONS}/${id}`;
  await axios.delete(REQUEST_URL);
}

export type RecipeQualitySpecDto = {
  id: number;
  recipeId: number;
  sortOrder: number;
  spec: string;
  method?: string | null;
  frequency?: string | null;
  acceptance?: string | null;
};

export async function postRecipeQualitySpecification(request: {
  recipeId: number;
  spec: string;
  method?: string;
  frequency?: string;
  acceptance?: string;
}): Promise<RecipeQualitySpecDto> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.RECIPE_QUALITY_SPECS}`;
  const RESPONSE = await axios.post(REQUEST_URL, request);
  const body = RESPONSE.data ?? {};
  if (body.status === 'error') {
    throw new Error(typeof body.message === 'string' ? body.message : 'Failed to save spec');
  }
  const inner =
    body.status === 'success' && body.data !== undefined ? body.data : body;
  return inner as RecipeQualitySpecDto;
}

export async function deleteRecipeQualitySpecification(id: number): Promise<void> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.RECIPE_QUALITY_SPECS}/${id}`;
  await axios.delete(REQUEST_URL);
}

export async function deleteRecipe(id: number): Promise<void> {
  const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.RECIPE_DELETE}/${id}`;
  await axios.delete(REQUEST_URL);
}

/** POST /recipe/save */
export async function postRecipe(request: RecipeSaveRequest): Promise<{
  id: number;
  status: string;
  workflowId: number;
}> {
  try {
    const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.RECIPE_SAVE}`;
    const RESPONSE = await axios.post(REQUEST_URL, request);
    const body = RESPONSE?.data ?? {};
    if (body.status === 'error') {
      throw new Error(
        typeof body.message === 'string' ? body.message : 'Failed to save recipe',
      );
    }
    const inner =
      body.status === 'success' && body.data !== undefined ? body.data : body;
    return inner as { id: number; status: string; workflowId: number };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { message?: string } | undefined;
      const msg = data?.message ?? error.message ?? 'Failed to save recipe';
      throw new Error(String(msg));
    }
    throw error;
  }
}

/**
 * Retrieves update data from the API  
 * @returns 
 */
export async function updateList(apiURL: string, request: any) {
    try {
        const REQUEST_URL = `${APP_CONFIG.API}${apiURL}`;        
        const RESPONSE = await axios.put(REQUEST_URL, request);
        const data = RESPONSE?.data?.status;

        return data;
    }
    catch(error) {
        return [];
    }
}

/**
 * Retrieves update data from the API  
 * @returns 
 */
export async function updateById(apiURL: string, request: any) {
    try {
        const REQUEST_URL = `${APP_CONFIG.API}${apiURL}/${request.id}`;        
        const RESPONSE = await axios.put(REQUEST_URL, request);
        const data = RESPONSE?.data?.status;

        return data;
    }
    catch(error) {
        return [];
    }
}

/**
 * Retrieves list data from the API
 */
/** Upload CAD file for a manual order; stores Azure Blob URL via backend. */
export async function uploadOrderCadFile(orderId: number, file: File) {
  try {
    const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.ORDERS_BASE}/${orderId}/cad-upload`;
    const formData = new FormData();
    formData.append('file', file);
    const RESPONSE = await axios.post(REQUEST_URL, formData);
    const body = RESPONSE?.data ?? {};
    const inner =
      typeof body === "object" &&
      body !== null &&
      (body as { status?: string }).status === "success" &&
      (body as { data?: unknown }).data !== undefined
        ? (body as { data: unknown }).data
        : body;
    return inner as { cad_file_url?: string };
  } catch (error) {
    throw error;
  }
}

export async function getVerify(apiURL: string) {
    try {
        const REQUEST_URL = `${APP_CONFIG.API}${apiURL}`;        
        const RESPONSE = await axios.get(REQUEST_URL);
        const data = RESPONSE?.data?.data;

        return data;
    }
    catch(error) {
        return false;
    }
}

/**
 * Fetch vendor owned machines from external partner service.
 * Used for machine type dropdown in Machines popup.
 */
export async function getPartnerVendorOwnedMachines() {
    try {
        const REQUEST_URL = `${APP_CONFIG.PARTNER_API_BASE}/vendor_owned_machines`;
        const RESPONSE = await axios.get(REQUEST_URL);

        // API shape can vary; normalize to array.
        const payload = RESPONSE?.data;
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.machines)) return payload.machines;
        return [];
    } catch (error) {
        return [];
    }
}
